import signingCompleteIllustration from "@/assets/img/signing-complete.svg";
import { LandingPageHeader } from "@/common/landingPageComponents";
import { Alert } from "@/components-v2/Alert";
import { BottomSpacer, Box, Container, VBox } from "@/components-v2/Box";
import { Button } from "@/components-v2/Button";
import { EmptyState } from "@/components-v2/EmptyState";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { Icon } from "@/components-v2/Icon";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { PdfPreview } from "@/components-v2/PdfPreview";
import { RadioButtonGroup } from "@/components-v2/RadioButton";
import { Select } from "@/components-v2/Select";
import { Tag } from "@/components-v2/Tag";
import { TextInput } from "@/components-v2/TextInput";
import { BoxProps } from "@/components-v2/boxHelpers";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { useTranslation } from "@/stores/RootStoreContext";
import { buttonRoleProps } from "@/utils/events";
import { handleEnter } from "@/utils/keyboardEvents";
import { getWindowDimensions } from "@/utils/other";
import { isMobileView } from "@/utils/screen";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { ReactSketchCanvas } from "react-sketch-canvas";
import WebFont from "webfontloader";
import {
  DoneStateUIState,
  HeaderUIState,
  ReadyUIState,
  SignDocumentPageUIState,
  signingFonts,
  SignWithEmailUIState,
  SignWithHandwritingUIState,
} from "./signDocumentDomain";
import styles from "./signingComponents.module.css";

const Header = observer((props: HeaderUIState) => <LandingPageHeader {...props.header} />);

export const SocialSecurityNumberInput = observer(
  ({
    socialSecurityNumber,
    startSigning,
  }: {
    socialSecurityNumber: NonNullable<ReadyUIState["socialSecurityNumber"]>;
    startSigning: ReadyUIState["startSigning"];
  }) => (
    <TextInput
      {...socialSecurityNumber}
      autofocus
      onKeyDown={handleEnter(() => !startSigning.disabled && startSigning.onClick())}
    />
  ),
);

export const SigningAlert = observer(({ alert }: { alert: NonNullable<ReadyUIState["alert"]> }) => (
  <Alert
    icon={false}
    centered
    type={alert.type}
    message={isMobileView() ? alert.mobileMessage : alert.message}
  />
));

const SigningFooter = observer(
  ({
    socialSecurityNumber,
    startSigning,
    ...boxProps
  }: Pick<ReadyUIState, "socialSecurityNumber" | "startSigning"> & BoxProps) => (
    <Box
      vertical={isMobileView()}
      justifyContent={"center"}
      alignItems={isMobileView() ? "stretch" : "center"}
      padding
      bg
      borderTop
      gap={"md"}
      {...boxProps}
    >
      {socialSecurityNumber &&
        (isMobileView() ? (
          <SocialSecurityNumberInput
            socialSecurityNumber={socialSecurityNumber}
            startSigning={startSigning}
          />
        ) : (
          <Box className={"max-w-80"} grow>
            <SocialSecurityNumberInput
              socialSecurityNumber={socialSecurityNumber}
              startSigning={startSigning}
            />
          </Box>
        ))}
      <Button
        variant={"primary"}
        className={socialSecurityNumber ? undefined : "sm:min-w-96"}
        {...startSigning}
      />
    </Box>
  ),
);

const ReadyView = observer(
  ({
    header,
    document,
    alert,
    socialSecurityNumber,
    startSigning,
    authorName,
    alertStore,
    documentTitle,
  }: ReadyUIState & { alertStore: AlertMessageStore }) => {
    const { t } = useTranslation();
    const topRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (alert?.type === "error") {
        topRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
      }
    }, [alert?.type]);
    return (
      <FullScreenLayout
        alertStore={alertStore}
        appBar={<Header {...header} />}
        centerPanel={
          <>
            <Alert
              ref={topRef}
              type={"info"}
              centered
              message={t(
                isMobileView()
                  ? "signing.document-details.text.mobile"
                  : "signing.document-details.text",
                {
                  authorName,
                  documentTitle,
                },
              )}
            />
            {alert && <SigningAlert alert={alert} />}
            <VBox container maxWidth="xl" marginTop>
              {document?.type === "pdf" && <PdfPreview file={document} width={"auto"} />}
            </VBox>
          </>
        }
        footerPanel={
          <SigningFooter socialSecurityNumber={socialSecurityNumber} startSigning={startSigning} />
        }
      />
    );
  },
);

const DoneView = observer(
  ({
    illustration,
    iconName,
    iconColor,
    title,
    description,
    primaryAction,
    requestGroups,
    continueInDCompany,
  }: DoneStateUIState) => {
    const { t } = useTranslation();
    return (
      <Box grow={"golden"} alignItems={"center"}>
        <Container maxWidth={"sm"} gap={"xl"}>
          <EmptyState
            illustration={
              illustration === "signing-complete" ? (
                <img src={signingCompleteIllustration} alt={""} width={170} />
              ) : (
                <Icon name={iconName} size={"jumbo"} color={iconColor} />
              )
            }
            title={title}
            description={description}
            actions={
              primaryAction && (
                <Button
                  variant={"primary"}
                  label={primaryAction.label}
                  onClick={primaryAction.onClick}
                />
              )
            }
          />
          {requestGroups?.map((group, groupIndex) => (
            <VBox key={groupIndex} gap={"sm"}>
              <span className={"subtle"}>{t(group.heading)}</span>
              <VBox bg border rounded>
                {group.requests.map((request, requestIndex) => (
                  <Box
                    key={requestIndex}
                    htmlProps={buttonRoleProps(request.onOpen)}
                    hoverBg
                    alignItems={"center"}
                    gap={"md"}
                    padding
                    borderBottom={requestIndex !== group.requests.length - 1}
                  >
                    <Icon name={"file-text"} size={"lg"} className={"subtle"} />
                    <VBox grow gap={"xs"}>
                      <span>{request.documentTitle}</span>
                      <span className={"subtle"}>{request.companyName}</span>
                    </VBox>
                    <Tag label={request.openLabel} color={"info"} />
                  </Box>
                ))}
              </VBox>
            </VBox>
          ))}
          {continueInDCompany && (
            <VBox alignItems={"center"} textAlign={"center"} gap={"md"}>
              <VBox gap={"xs"}>
                <strong>{t(continueInDCompany.title)}</strong>
                <span>{t(continueInDCompany.description)}</span>
              </VBox>
              <Button
                variant={"secondary"}
                label={continueInDCompany.label}
                onClick={continueInDCompany.onClick}
              />
            </VBox>
          )}
          <BottomSpacer />
        </Container>
      </Box>
    );
  },
);

export const SignWithEmailView = observer(
  ({ message, verificationCode, submitVerificationCode }: SignWithEmailUIState) => (
    <Container maxWidth={"sm"} marginTop={"md"}>
      <Alert
        icon={{ size: "lg", name: "warning", weight: "regular" }}
        type={"info"}
        message={message}
      />
      <TextInput {...verificationCode} />
      <Button variant={"primary"} {...submitVerificationCode} />
    </Container>
  ),
);

export const SignWithHandwritingView = observer(
  ({
    message,
    inputMode,
    font,
    typedText,
    submit,
    setReactSketchCanvasRef,
    validationError,
  }: SignWithHandwritingUIState) => {
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

    useEffect(() => {
      const handleResize = () => {
        setWindowDimensions(getWindowDimensions());
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
      WebFont.load({
        google: {
          families: signingFonts,
        },
      });
    }, []);

    return (
      <Container maxWidth={"lg"}>
        <VBox marginTop={"md"} alignItems={"center"}>
          <Alert icon={{ name: "warning", size: "lg" }} type={"info"} message={message} />
          <RadioButtonGroup horizontal={!isMobileView()} {...inputMode} />
          {inputMode.selected === "type" ? (
            <>
              <Select {...font} />
              <input
                className={styles.textInput}
                style={{ fontFamily: `'${font.value}', cursive` }}
                {...typedText}
              />
            </>
          ) : (
            <ReactSketchCanvas
              ref={setReactSketchCanvasRef}
              className={styles.drawCanvas}
              width="100%"
              height="400px"
              strokeWidth={windowDimensions.width / 350}
              strokeColor="darkblue"
              exportWithBackgroundImage={false}
            />
          )}
          {validationError && <Alert icon message={validationError} type={"error"} />}
          {submit && <Button variant={"primary"} {...submit} />}
        </VBox>
      </Container>
    );
  },
);

export const SignDocumentPageView = observer(
  ({ state, alertStore }: { state: SignDocumentPageUIState; alertStore: AlertMessageStore }) => {
    switch (state.type) {
      case "loading":
        return (
          <FullScreenLayout
            alertStore={alertStore}
            appBar={<Header {...state.header} />}
            centerPanel={<LoadingIndicator fullscreen />}
          />
        );
      case "ready":
        return <ReadyView alertStore={alertStore} {...state} />;
      case "sign-with-email":
        return (
          <FullScreenLayout
            alertStore={alertStore}
            appBar={<Header {...state.header} />}
            centerPanel={<SignWithEmailView {...state} />}
          />
        );
      case "sign-with-handwriting":
        return (
          <FullScreenLayout
            alertStore={alertStore}
            appBar={<Header {...state.header} />}
            centerPanel={<SignWithHandwritingView {...state} />}
          />
        );
      case "done":
        return (
          <FullScreenLayout
            alertStore={alertStore}
            appBar={<Header {...state.header} />}
            centerPanel={<DoneView {...state} />}
          />
        );
    }
  },
);
